<template>
  <header
    v-if="!compact"
    class="sticky top-0 z-20 w-full min-w-0 px-4 py-3 border-b bg-white/95 dark:bg-slate-900/95 border-slate-200/70 dark:border-slate-800/80 backdrop-blur-md"
  >
    <div
      class="flex items-stretch justify-between gap-3 sm:flex-row sm:items-center sm:gap-4"
    >
      <!-- LEFT: toggle + title -->
      <div class="flex items-center order-1 gap-2 shrink-0">
        <button
          class="inline-flex items-center justify-center transition w-9 h-9 rounded-xl bg-primary/5 text-primary hover:bg-primary/10 focus:outline-none"
          @click="emit('toggle-sidebar')"
        >
          <i
            class="text-[20px] mdi"
            :class="isCollapsed ? 'mdi-menu-open' : 'mdi-menu'"
          ></i>
        </button>

        <span
          class="hidden text-sm font-semibold text-slate-800 dark:text-slate-100 sm:inline"
        >
          <!-- Title placeholder -->
        </span>
      </div>

      <!-- RIGHT: lang + bell + account -->
      <div
        class="flex items-center justify-end order-2 gap-2 shrink-0 sm:order-3"
      >
        <!-- Language Switcher -->
        <div
          class="inline-flex items-center gap-1 px-1 rounded-full h-9 bg-white/70 dark:bg-slate-900/70 backdrop-blur ring-1 ring-slate-200 dark:ring-slate-700"
        >
          <button
            type="button"
            class="px-3 text-xs font-semibold transition rounded-full h-7 hover:bg-slate-200/60 dark:hover:bg-slate-700/60"
            :class="
              lang === 'ar'
                ? 'bg-secondary/60 text-primary'
                : 'text-slate-700 dark:text-slate-300'
            "
            aria-label="Arabic"
            @click="setLang('ar')"
          >
            ع
          </button>

          <button
            type="button"
            class="px-3 text-xs font-semibold transition rounded-full h-7 hover:bg-slate-200/60 dark:hover:bg-slate-700/60"
            :class="
              lang === 'en'
                ? 'bg-secondary/60 text-primary'
                : 'text-slate-700 dark:text-slate-300'
            "
            aria-label="English"
            @click="setLang('en')"
          >
            EN
          </button>
        </div>

        <NotificationBell :lang="lang" :t="t" />

        <!-- Account Wrapper -->
        <div class="relative" ref="accountWrapper">
          <button
            class="flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium transition-all duration-200 rounded-full bg-slate-100/90 dark:bg-slate-800/90 hover:bg-slate-200/80 dark:hover:bg-slate-700/80 text-slate-700 dark:text-slate-200 ring-1 ring-slate-200/80 dark:ring-slate-700/80 shadow-xs focus:outline-none focus:ring-2 focus:ring-primary/40"
            @click.stop="toggleAccountMenu"
            :aria-expanded="isAccountMenuOpen"
          >
            <div class="relative shrink-0">
              <div
                class="flex items-center justify-center w-8 h-8 overflow-hidden text-xs font-bold text-white rounded-full bg-gradient-to-tr from-primary to-emerald-600 ring-2 ring-white dark:ring-slate-900 shadow-xs"
              >
                <img
                  v-if="avatarSrc"
                  :src="avatarSrc"
                  :alt="avatarName"
                  class="object-cover w-8 h-8 rounded-full"
                />
                <span v-else>{{ avatarInitials }}</span>
              </div>
              <span
                class="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full ring-2 ring-white dark:ring-slate-900"
              ></span>
            </div>

            <div class="hidden text-start max-w-[120px] sm:flex sm:flex-col min-w-0">
              <span class="font-semibold truncate text-slate-900 dark:text-slate-100 text-xs leading-tight">
                {{ avatarName || t("admin_consulting_manager") }}
              </span>
              <span class="text-[10px] text-slate-500 dark:text-slate-400 truncate leading-tight mt-0.5">
                {{ roleLabel }}
              </span>
            </div>

            <i
              class="mdi mdi-chevron-down text-[16px] text-slate-400 transition-transform duration-300 ms-0.5"
              :class="{ 'rotate-180': isAccountMenuOpen }"
            ></i>
          </button>

          <!-- Desktop Dropdown Menu -->
          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform opacity-0 scale-95 -translate-y-2"
            enter-to-class="transform opacity-100 scale-100 translate-y-0"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="transform opacity-100 scale-100 translate-y-0"
            leave-to-class="transform opacity-0 scale-95 -translate-y-2"
          >
            <div
              v-if="isAccountMenuOpen"
              class="hidden sm:block fixed z-50 w-72 p-2 overflow-hidden text-sm bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-800 shadow-2xl shadow-slate-900/10 dark:shadow-slate-950/50 rounded-2xl backdrop-blur-xl"
              :style="menuStyles"
            >
              <!-- Profile Header Card inside Menu -->
              <div class="p-3 mb-1.5 rounded-xl bg-gradient-to-br from-primary/5 via-slate-50/80 to-primary/10 dark:from-primary/15 dark:via-slate-800/60 dark:to-slate-800/30 border border-slate-100 dark:border-slate-800/80">
                <div class="flex items-center gap-3">
                  <div class="relative shrink-0">
                    <div class="flex items-center justify-center w-11 h-11 text-sm font-bold text-white rounded-full bg-gradient-to-tr from-primary to-emerald-600 ring-2 ring-white dark:ring-slate-800 shadow-sm overflow-hidden">
                      <img v-if="avatarSrc" :src="avatarSrc" :alt="avatarName" class="object-cover w-full h-full" />
                      <span v-else>{{ avatarInitials }}</span>
                    </div>
                    <span class="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 rounded-full ring-2 ring-white dark:ring-slate-800"></span>
                  </div>
                  <div class="flex flex-col min-w-0 flex-1">
                    <span class="font-bold text-slate-900 dark:text-slate-100 text-sm truncate leading-snug">
                      {{ avatarName || t("admin_consulting_manager") }}
                    </span>
                    <span v-if="avatarEmail" class="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5">
                      {{ avatarEmail }}
                    </span>
                    <div class="mt-1">
                      <span class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-light border border-primary/20">
                        <i class="mdi mdi-shield-check-outline text-[12px]"></i>
                        {{ roleLabel }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Menu Items -->
              <div class="space-y-0.5">
                <button
                  class="flex items-center justify-between w-full px-3 py-2.5 text-xs font-medium rounded-xl transition-all duration-150 text-slate-700 dark:text-slate-200 hover:bg-slate-100/90 dark:hover:bg-slate-800/80 group cursor-pointer"
                  @click="goToDashboard"
                >
                  <div class="flex items-center gap-2.5">
                    <div class="flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:bg-primary group-hover:text-white transition-colors">
                      <i class="text-[16px] mdi mdi-view-dashboard-outline"></i>
                    </div>
                    <span>{{ t("Dashboard") || "Dashboard" }}</span>
                  </div>
                  <i class="mdi text-[14px] text-slate-400 group-hover:text-primary transition-colors" :class="lang === 'ar' ? 'mdi-chevron-left' : 'mdi-chevron-right'"></i>
                </button>

                <button
                  class="flex items-center justify-between w-full px-3 py-2.5 text-xs font-medium rounded-xl transition-all duration-150 text-slate-700 dark:text-slate-200 hover:bg-slate-100/90 dark:hover:bg-slate-800/80 group cursor-pointer"
                  @click="goToProfile"
                >
                  <div class="flex items-center gap-2.5">
                    <div class="flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:bg-primary group-hover:text-white transition-colors">
                      <i class="text-[16px] mdi mdi-account-outline"></i>
                    </div>
                    <span>{{ t("menu_profile") }}</span>
                  </div>
                  <i class="mdi text-[14px] text-slate-400 group-hover:text-primary transition-colors" :class="lang === 'ar' ? 'mdi-chevron-left' : 'mdi-chevron-right'"></i>
                </button>

                <button
                  class="flex items-center justify-between w-full px-3 py-2.5 text-xs font-medium rounded-xl transition-all duration-150 text-slate-700 dark:text-slate-200 hover:bg-slate-100/90 dark:hover:bg-slate-800/80 group cursor-pointer"
                  @click="goToSettings"
                >
                  <div class="flex items-center gap-2.5">
                    <div class="flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:bg-primary group-hover:text-white transition-colors">
                      <i class="text-[16px] mdi mdi-tune-variant"></i>
                    </div>
                    <span>{{ t("menu_account_settings") }}</span>
                  </div>
                  <i class="mdi text-[14px] text-slate-400 group-hover:text-primary transition-colors" :class="lang === 'ar' ? 'mdi-chevron-left' : 'mdi-chevron-right'"></i>
                </button>
              </div>

              <div class="my-1.5 border-t border-slate-100 dark:border-slate-800"></div>

              <!-- Logout Button -->
              <button
                class="flex items-center justify-between w-full px-3 py-2.5 text-xs font-medium rounded-xl transition-all duration-150 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 group cursor-pointer"
                @click="logout"
              >
                <div class="flex items-center gap-2.5">
                  <div class="flex items-center justify-center w-7 h-7 rounded-lg bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 group-hover:bg-rose-600 group-hover:text-white transition-colors">
                    <i class="text-[16px] mdi mdi-logout-variant"></i>
                  </div>
                  <span class="font-semibold">{{ t("menu_logout") }}</span>
                </div>
                <i class="mdi mdi-arrow-right-thin text-[16px] text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity" :class="lang === 'ar' ? 'rotate-180' : ''"></i>
              </button>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </header>

  <!-- Compact account menu (used inside Navbar) -->
  <div v-else class="flex items-center gap-2">
    <NotificationBell :lang="lang" :t="t" />

    <div class="relative" ref="accountWrapper">
      <button
        class="flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium transition-all duration-200 rounded-full bg-slate-100/90 dark:bg-slate-800/90 hover:bg-slate-200/80 dark:hover:bg-slate-700/80 text-slate-700 dark:text-slate-200 ring-1 ring-slate-200/80 dark:ring-slate-700/80 shadow-xs focus:outline-none focus:ring-2 focus:ring-primary/40"
        @click.stop="toggleAccountMenu"
        :aria-expanded="isAccountMenuOpen"
      >
        <div class="relative shrink-0">
          <div
            class="flex items-center justify-center w-8 h-8 overflow-hidden text-xs font-bold text-white rounded-full bg-gradient-to-tr from-primary to-emerald-600 ring-2 ring-white dark:ring-slate-900 shadow-xs"
          >
            <img
              v-if="avatarSrc"
              :src="avatarSrc"
              :alt="avatarName"
              class="object-cover w-8 h-8 rounded-full"
            />
            <span v-else>{{ avatarInitials }}</span>
          </div>
          <span
            class="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full ring-2 ring-white dark:ring-slate-900"
          ></span>
        </div>

        <div class="hidden text-start max-w-[120px] sm:flex sm:flex-col min-w-0">
          <span class="font-semibold truncate text-slate-900 dark:text-slate-100 text-xs leading-tight">
            {{ avatarName || t("admin_consulting_manager") }}
          </span>
          <span class="text-[10px] text-slate-500 dark:text-slate-400 truncate leading-tight mt-0.5">
            {{ roleLabel }}
          </span>
        </div>

        <i
          class="mdi mdi-chevron-down text-[16px] text-slate-400 transition-transform duration-300 ms-0.5"
          :class="{ 'rotate-180': isAccountMenuOpen }"
        ></i>
      </button>

      <!-- Desktop Dropdown Menu (Compact Mode) -->
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="transform opacity-0 scale-95 -translate-y-2"
        enter-to-class="transform opacity-100 scale-100 translate-y-0"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="transform opacity-100 scale-100 translate-y-0"
        leave-to-class="transform opacity-0 scale-95 -translate-y-2"
      >
        <div
          v-if="isAccountMenuOpen"
          class="hidden sm:block fixed z-50 w-72 p-2 overflow-hidden text-sm bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-800 shadow-2xl shadow-slate-900/10 dark:shadow-slate-950/50 rounded-2xl backdrop-blur-xl"
          :style="menuStyles"
        >
          <!-- Profile Header Card inside Menu -->
          <div class="p-3 mb-1.5 rounded-xl bg-gradient-to-br from-primary/5 via-slate-50/80 to-primary/10 dark:from-primary/15 dark:via-slate-800/60 dark:to-slate-800/30 border border-slate-100 dark:border-slate-800/80">
            <div class="flex items-center gap-3">
              <div class="relative shrink-0">
                <div class="flex items-center justify-center w-11 h-11 text-sm font-bold text-white rounded-full bg-gradient-to-tr from-primary to-emerald-600 ring-2 ring-white dark:ring-slate-800 shadow-sm overflow-hidden">
                  <img v-if="avatarSrc" :src="avatarSrc" :alt="avatarName" class="object-cover w-full h-full" />
                  <span v-else>{{ avatarInitials }}</span>
                </div>
                <span class="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 rounded-full ring-2 ring-white dark:ring-slate-800"></span>
              </div>
              <div class="flex flex-col min-w-0 flex-1">
                <span class="font-bold text-slate-900 dark:text-slate-100 text-sm truncate leading-snug">
                  {{ avatarName || t("admin_consulting_manager") }}
                </span>
                <span v-if="avatarEmail" class="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5">
                  {{ avatarEmail }}
                </span>
                <div class="mt-1">
                  <span class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-light border border-primary/20">
                    <i class="mdi mdi-shield-check-outline text-[12px]"></i>
                    {{ roleLabel }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Menu Items -->
          <div class="space-y-0.5">
            <button
              class="flex items-center justify-between w-full px-3 py-2.5 text-xs font-medium rounded-xl transition-all duration-150 text-slate-700 dark:text-slate-200 hover:bg-slate-100/90 dark:hover:bg-slate-800/80 group cursor-pointer"
              @click="goToDashboard"
            >
              <div class="flex items-center gap-2.5">
                <div class="flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:bg-primary group-hover:text-white transition-colors">
                  <i class="text-[16px] mdi mdi-view-dashboard-outline"></i>
                </div>
                <span>{{ t("Dashboard") || "Dashboard" }}</span>
              </div>
              <i class="mdi text-[14px] text-slate-400 group-hover:text-primary transition-colors" :class="lang === 'ar' ? 'mdi-chevron-left' : 'mdi-chevron-right'"></i>
            </button>

            <button
              class="flex items-center justify-between w-full px-3 py-2.5 text-xs font-medium rounded-xl transition-all duration-150 text-slate-700 dark:text-slate-200 hover:bg-slate-100/90 dark:hover:bg-slate-800/80 group cursor-pointer"
              @click="goToProfile"
            >
              <div class="flex items-center gap-2.5">
                <div class="flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:bg-primary group-hover:text-white transition-colors">
                  <i class="text-[16px] mdi mdi-account-outline"></i>
                </div>
                <span>{{ t("menu_profile") }}</span>
              </div>
              <i class="mdi text-[14px] text-slate-400 group-hover:text-primary transition-colors" :class="lang === 'ar' ? 'mdi-chevron-left' : 'mdi-chevron-right'"></i>
            </button>

            <button
              class="flex items-center justify-between w-full px-3 py-2.5 text-xs font-medium rounded-xl transition-all duration-150 text-slate-700 dark:text-slate-200 hover:bg-slate-100/90 dark:hover:bg-slate-800/80 group cursor-pointer"
              @click="goToSettings"
            >
              <div class="flex items-center gap-2.5">
                <div class="flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:bg-primary group-hover:text-white transition-colors">
                  <i class="text-[16px] mdi mdi-tune-variant"></i>
                </div>
                <span>{{ t("menu_account_settings") }}</span>
              </div>
              <i class="mdi text-[14px] text-slate-400 group-hover:text-primary transition-colors" :class="lang === 'ar' ? 'mdi-chevron-left' : 'mdi-chevron-right'"></i>
            </button>
          </div>

          <div class="my-1.5 border-t border-slate-100 dark:border-slate-800"></div>

          <!-- Logout Button -->
          <button
            class="flex items-center justify-between w-full px-3 py-2.5 text-xs font-medium rounded-xl transition-all duration-150 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 group cursor-pointer"
            @click="logout"
          >
            <div class="flex items-center gap-2.5">
              <div class="flex items-center justify-center w-7 h-7 rounded-lg bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 group-hover:bg-rose-600 group-hover:text-white transition-colors">
                <i class="text-[16px] mdi mdi-logout-variant"></i>
              </div>
              <span class="font-semibold">{{ t("menu_logout") }}</span>
            </div>
            <i class="mdi mdi-arrow-right-thin text-[16px] text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity" :class="lang === 'ar' ? 'rotate-180' : ''"></i>
          </button>
        </div>
      </transition>
    </div>
  </div>

  <!-- Mobile Bottom Drawer Sheet (< 640px) -->
  <teleport to="body">
    <div v-if="isAccountMenuOpen" class="sm:hidden">
      <!-- Backdrop Overlay -->
      <transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          class="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs"
          @click="isAccountMenuOpen = false"
        ></div>
      </transition>

      <!-- Bottom Sheet Drawer -->
      <transition
        enter-active-class="transition duration-300 ease-out transform"
        enter-from-class="translate-y-full"
        enter-to-class="translate-y-0"
        leave-active-class="transition duration-200 ease-in transform"
        leave-from-class="translate-y-0"
        leave-to-class="translate-y-full"
      >
        <div
          class="fixed bottom-0 inset-x-0 z-50 p-4 pb-8 bg-white dark:bg-slate-900 rounded-t-3xl border-t border-slate-200 dark:border-slate-800 shadow-2xl space-y-3 max-h-[85vh] overflow-y-auto"
          :dir="lang === 'ar' ? 'rtl' : 'ltr'"
        >
          <!-- Drag Handle -->
          <div class="w-12 h-1 bg-slate-300 dark:bg-slate-700 rounded-full mx-auto mb-2"></div>

          <!-- User Card Header -->
          <div class="p-3.5 rounded-2xl bg-gradient-to-br from-primary/10 via-slate-50 to-primary/5 dark:from-primary/20 dark:via-slate-800 dark:to-slate-800/50 border border-slate-100 dark:border-slate-800">
            <div class="flex items-center justify-between gap-3">
              <div class="flex items-center gap-3 min-w-0">
                <div class="relative shrink-0">
                  <div class="flex items-center justify-center w-12 h-12 text-sm font-bold text-white rounded-full bg-gradient-to-tr from-primary to-emerald-600 ring-2 ring-white dark:ring-slate-800 shadow-md overflow-hidden">
                    <img v-if="avatarSrc" :src="avatarSrc" :alt="avatarName" class="object-cover w-full h-full" />
                    <span v-else>{{ avatarInitials }}</span>
                  </div>
                  <span class="absolute bottom-0 right-0 w-3.5 h-3.5 bg-emerald-500 rounded-full ring-2 ring-white dark:ring-slate-800"></span>
                </div>
                <div class="flex flex-col min-w-0">
                  <span class="font-bold text-slate-900 dark:text-slate-100 text-base truncate">
                    {{ avatarName || t("admin_consulting_manager") }}
                  </span>
                  <span v-if="avatarEmail" class="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {{ avatarEmail }}
                  </span>
                  <div class="mt-1">
                    <span class="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-semibold rounded-full bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-light border border-primary/20">
                      <i class="mdi mdi-shield-check-outline text-[13px]"></i>
                      {{ roleLabel }}
                    </span>
                  </div>
                </div>
              </div>

              <button
                class="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 bg-slate-100 dark:bg-slate-800 shrink-0"
                @click="isAccountMenuOpen = false"
              >
                <i class="mdi mdi-close text-lg"></i>
              </button>
            </div>
          </div>

          <!-- Touch Friendly Mobile Action List -->
          <div class="space-y-1.5 pt-1">
            <button
              class="flex items-center justify-between w-full p-3 min-h-[48px] rounded-2xl transition-colors text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800"
              @click="goToDashboard"
            >
              <div class="flex items-center gap-3">
                <div class="flex items-center justify-center w-9 h-9 rounded-xl bg-primary/10 text-primary dark:bg-primary/20">
                  <i class="text-lg mdi mdi-view-dashboard-outline"></i>
                </div>
                <span class="font-medium text-sm">{{ t("Dashboard") || "Dashboard" }}</span>
              </div>
              <i class="mdi text-base text-slate-400" :class="lang === 'ar' ? 'mdi-chevron-left' : 'mdi-chevron-right'"></i>
            </button>

            <button
              class="flex items-center justify-between w-full p-3 min-h-[48px] rounded-2xl transition-colors text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800"
              @click="goToProfile"
            >
              <div class="flex items-center gap-3">
                <div class="flex items-center justify-center w-9 h-9 rounded-xl bg-primary/10 text-primary dark:bg-primary/20">
                  <i class="text-lg mdi mdi-account-outline"></i>
                </div>
                <span class="font-medium text-sm">{{ t("menu_profile") }}</span>
              </div>
              <i class="mdi text-base text-slate-400" :class="lang === 'ar' ? 'mdi-chevron-left' : 'mdi-chevron-right'"></i>
            </button>

            <button
              class="flex items-center justify-between w-full p-3 min-h-[48px] rounded-2xl transition-colors text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800"
              @click="goToSettings"
            >
              <div class="flex items-center gap-3">
                <div class="flex items-center justify-center w-9 h-9 rounded-xl bg-primary/10 text-primary dark:bg-primary/20">
                  <i class="text-lg mdi mdi-tune-variant"></i>
                </div>
                <span class="font-medium text-sm">{{ t("menu_account_settings") }}</span>
              </div>
              <i class="mdi text-base text-slate-400" :class="lang === 'ar' ? 'mdi-chevron-left' : 'mdi-chevron-right'"></i>
            </button>

            <div class="pt-2">
              <button
                class="flex items-center justify-between w-full p-3 min-h-[48px] rounded-2xl transition-colors text-rose-600 dark:text-rose-400 bg-rose-50/80 dark:bg-rose-950/30 hover:bg-rose-100 dark:hover:bg-rose-900/40"
                @click="logout"
              >
                <div class="flex items-center gap-3">
                  <div class="flex items-center justify-center w-9 h-9 rounded-xl bg-rose-100 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400">
                    <i class="text-lg mdi mdi-logout-variant"></i>
                  </div>
                  <span class="font-semibold text-sm">{{ t("menu_logout") }}</span>
                </div>
                <i class="mdi text-base text-rose-500" :class="lang === 'ar' ? 'mdi-chevron-left' : 'mdi-chevron-right'"></i>
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </teleport>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, toRefs } from "vue";
import { useRouter } from "vue-router";
import api from "@/services/api";
import { clearAuthCookies } from "@/utils/authSession";
import { clearPendingVerification } from "@/utils/pendingVerification";
import { useRoleLabel } from "@/composables/useRoleLabel";
import NotificationBell from "@/components/notifications/NotificationBell.vue";
import { useNotificationStore } from "@/stores/notifications";
import { setAppLang, normalizeLang } from "@/i18n";

// ===== props & emits =====
const props = defineProps({
  isCollapsed: { type: Boolean, default: false },
  lang: { type: String, default: "ar" },
  t: { type: Function, required: true },
  compact: { type: Boolean, default: false },
});

const emit = defineEmits(["toggle-sidebar", "update:lang"]);
const { lang, t, isCollapsed, compact } = toRefs(props);

// ===== state =====
const isAccountMenuOpen = ref(false);
const accountWrapper = ref(null);
const router = useRouter();
const notificationStore = useNotificationStore();

// 🟢 user object loaded from cookie
const user = ref(null);

// ===== helpers: cookies =====
function getCookie(name) {
  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"
    )
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function loadUserFromCookie() {
  const raw = getCookie("auth_user");
  if (!raw) {
    user.value = null;
    return;
  }

  try {
    user.value = JSON.parse(raw);
  } catch (e) {
    console.error("Failed to parse auth_user cookie in Topbar", e);
    user.value = null;
  }
}

// ===== computed from user =====
const avatarName = computed(() => user.value?.name || "");
const avatarEmail = computed(
  () => user.value?.email || user.value?.phone || user.value?.username || ""
);
const roleSource = computed(() => user.value?.role || user.value?.type || "");
const roleLabel = useRoleLabel(roleSource, lang);

const avatarInitials = computed(() => {
  if (!avatarName.value) return "AD";
  const parts = avatarName.value.trim().split(" ");
  const first = (parts[0] || "").charAt(0);
  const second = (parts[1] || "").charAt(0);
  const initials = (first + second).trim();
  return (initials || first || "AD").toUpperCase();
});

const avatarSrc = computed(() => {
  return user.value?.avatar || user.value?.avatar_url || user.value?.office?.logo || "";
});

// ===== methods =====
const setLang = (next) => {
  const final = normalizeLang(next);
  setAppLang(final);
  emit("update:lang", final);
};

const menuStyles = ref({});

const updateMenuPosition = () => {
  if (!isAccountMenuOpen.value || !accountWrapper.value) return;

  const rect = accountWrapper.value.getBoundingClientRect();

  const menuWidth = 288; // 288px (w-72) for ample legible spacing
  const gap = 10;
  const edge = 16;
  const isRtl = lang.value === "ar" || document.documentElement.dir === "rtl";

  if (isRtl) {
    let left = rect.right - menuWidth;

    const maxLeft = window.innerWidth - menuWidth - edge;
    if (left > maxLeft) left = maxLeft;
    if (left < edge) left = edge;

    menuStyles.value = {
      top: rect.bottom + gap + "px",
      left: left + "px",
      right: "auto",
    };
  } else {
    let right = window.innerWidth - rect.right;

    const maxRight = window.innerWidth - menuWidth - edge;
    if (right > maxRight) right = maxRight;
    if (right < edge) right = edge;

    menuStyles.value = {
      top: rect.bottom + gap + "px",
      right: right + "px",
      left: "auto",
    };
  }
};

const toggleAccountMenu = () => {
  isAccountMenuOpen.value = !isAccountMenuOpen.value;
  if (isAccountMenuOpen.value) {
    updateMenuPosition();
  }
};

const handleClickOutside = (event) => {
  const wrapper = accountWrapper.value;
  if (wrapper && !wrapper.contains(event.target)) {
    isAccountMenuOpen.value = false;
  }
};

const handleWindowResize = () => {
  if (isAccountMenuOpen.value) {
    updateMenuPosition();
  }
};

const handleWindowScroll = () => {
  if (isAccountMenuOpen.value) {
    updateMenuPosition();
  }
};

const goToProfile = () => {
  isAccountMenuOpen.value = false;
  router.push({ name: "UserProfile" });
};

const dashboardRoute = computed(() => "/account/profile");

const goToDashboard = () => {
  isAccountMenuOpen.value = false;
  router.push(dashboardRoute.value);
};

const rawRole = computed(() =>
  String(user.value?.role || user.value?.type || "").toLowerCase()
);

const settingsRoute = computed(() => {
  const role = rawRole.value;

  if (role.includes("office")) return "/account/settings/office";
  if (role.includes("consultant")) return "/account/settings/consultant";
  return "/account/settings/client";
});

const goToSettings = () => {
  isAccountMenuOpen.value = false;
  router.push(settingsRoute.value);
};

function deleteCookie(name) {
  document.cookie = `${name}=; Max-Age=0; path=/;`;
}

const logout = async () => {
  isAccountMenuOpen.value = false;

  try {
    await api.post("/logout", null, {
      headers: { Accept: "application/json" },
    });
  } catch (e) {
    console.error("Logout API failed:", e);
  }

  deleteCookie("auth_token");
  deleteCookie("auth_user");
  clearAuthCookies();
  clearPendingVerification();
  localStorage.removeItem("token");
  notificationStore.reset();

  router.replace("/login");
};

// ===== lifecycle =====
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("resize", handleWindowResize, { passive: true });
  window.addEventListener("scroll", handleWindowScroll, { passive: true });

  loadUserFromCookie();
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("resize", handleWindowResize);
  window.removeEventListener("scroll", handleWindowScroll);
});
</script>

